import React, { useEffect, useMemo } from "react";
import Table from "../../../../../common/element/Table";
import ToolManagmentCard from "../../../../../common/element/Card/ToolManagmnetCard";
import { useDispatch, useSelector } from "react-redux";
import { getCompanyEmployeeDetail } from "../../../../../redux/ProspectCompany/companyProspectSlice";
import {
    selectCompanyEmployeeDetail,
    selectCompanyEmployeeDetailLoading
} from "../../../../../redux/ProspectCompany/companyProspectSelectors";
import ThreeDotBouncingLoader from "../../../../../common/element/ButtonLoader/ThreeDotBouncingLoder";

const { Tr, Th, Td, THead, TBody } = Table;

const CompanyEmployeeView = ({ onBack, companyId, candidateId }) => {
    const dispatch = useDispatch();
    const employeeDetail = useSelector(selectCompanyEmployeeDetail);
    const isLoading = useSelector(selectCompanyEmployeeDetailLoading);

    useEffect(() => {
        if (companyId && candidateId) {
            dispatch(getCompanyEmployeeDetail({ companyId, employeeId: candidateId }));
        }
    }, [dispatch, companyId, candidateId]);

    // Ensure the employee name is capitalized (first letter of each word)
    const capitalizeName = (n) => {
        if (!n) return n;
        return String(n)
            .split(' ')
            .filter(Boolean)
            .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
            .join(' ');
    };

    const rawName = (employeeDetail?.name && String(employeeDetail.name).trim()) || `${employeeDetail?.first_name || ""} ${employeeDetail?.last_name || ""}`.trim();

    const employeeData = {
        name: capitalizeName(rawName) || "-",
        mobile: employeeDetail?.mobile || employeeDetail?.phone || "-",
        email: employeeDetail?.email || employeeDetail?.user_email || "-",
        city: employeeDetail?.city,
        workField: employeeDetail?.work_field
    };

    const companyData = {
        name: employeeDetail?.company?.company_name || "-",
        email: employeeDetail?.company?.contact_email || "-",
        phone: employeeDetail?.company?.contact_phone || "-",
        address: employeeDetail?.company?.address?.replace(/\/n/g, " ") || "-"
    };

    // Normalize data and detect tool by id (preferred) or name
    const toolId = employeeDetail?.tool?.id || employeeDetail?.tool_id || employeeDetail?.result?.tool_id || null;
    const toolName = String(employeeDetail?.tool?.name || "").toLowerCase();

    const isNCodesTool = toolId === 1 || toolId === 2 || toolId === 3 || toolName.includes("ncode");
    const isBaselineResult = toolId === 5;
    const isHumanNeedsResult = toolId === 4 || toolId === 8;
    const isPersonalityTestResult = toolId === 6;
    const isTraitsTestResult = toolId === 7;

    // score payload locations differ across APIs; normalize
    const scoreJson = employeeDetail?.result?.score_json || employeeDetail?.result?.score || employeeDetail?.result || {};

    // Selected codes for N Codes
    const ncodeSelected = scoreJson?.selected_codes || employeeDetail?.result?.selected_codes || [];

    // For personality-like generic list
    const personalityTypes = employeeDetail?.results || scoreJson?.selected_codes || [];

    // Traits rows (tool 7) derived from score object if present
    const traitsTestRows = useMemo(() => {
        if (isTraitsTestResult && scoreJson) {
            const scoreData = scoreJson;
            const traits = [];
            const traitNames = Object.keys(scoreData)
                .filter(key => key.endsWith('Level'))
                .map(key => key.replace('Level', ''));

            traitNames.forEach(trait => {
                const levelKey = `${trait}Level`;
                const scoreKey = `${trait}Score`;
                traits.push({
                    name: trait.charAt(0).toUpperCase() + trait.slice(1),
                    level: scoreData[levelKey] || '',
                    score: scoreData[scoreKey] || 0
                });
            });

            return traits.sort((a, b) => b.score - a.score);
        }
        return [];
    }, [isTraitsTestResult, scoreJson]);

    // Personality test rows (tool 6) derived and sorted desc
    const personalityTestRows = useMemo(() => {
        if (isPersonalityTestResult && scoreJson) {
            return Object.entries(scoreJson)
                .map(([key, value]) => ({ key, value }))
                .sort((a, b) => b.value - a.value);
        }
        return [];
    }, [isPersonalityTestResult, scoreJson]);

    const highestScore = personalityTestRows.length > 0 ? personalityTestRows[0]?.value : 0;
    const highestTraitScore = traitsTestRows.length > 0 ? traitsTestRows[0]?.score : 0;

    // Human needs / inner drive rows (tool 4 and 8)
    const humanNeedsRows = useMemo(() => {
        const scoreData = scoreJson || {};
        const rows = [
            { key: 'Certainty', value: scoreData?.Certainty || 0 },
            { key: 'Variety', value: scoreData?.Variety || 0 },
            { key: 'Significance', value: scoreData?.Significance || 0 },
            { key: 'Love/Connection', value: scoreData?.['Love/Connection'] || 0 },
            { key: 'Growth', value: scoreData?.Growth || 0 },
            { key: 'Contribution', value: scoreData?.Contribution || 0 },
        ];
        return rows.sort((a, b) => b.value - a.value);
    }, [scoreJson]);

    // Render per-section loaders so each card shows its own loader

    return (
        <div className="min-h-screen w-full ">
            {/* --- Header --- */}
            <div className="flex justify-between items-center mb-8">
                <h2 className="text-2xl font-bold text-gray-800">{employeeData.name || 'Employee View'}</h2>
            </div>

            {/* --- Top Information Cards --- */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8 text-start">

                {/* Employee Information Card */}
                <ToolManagmentCard>
                    <h3 className="text-base font-bold text-gray-700 mb-3 text-start">Employee information</h3>
                    <div className="border-b border-gray-100 mb-4"></div>

                    {isLoading ? (
                        <div className="flex items-center justify-center py-6">
                            <ThreeDotBouncingLoader />
                        </div>
                    ) : (
                        <div className="space-y-6">
                            {/* Name Row */}
                            <div>
                                <p className="font-medium text-[14px] leading-[150%] text-[#98A2B3]">Name</p>
                                <p className="font-medium text-[16px] leading-[150%] text-[#101828]">{employeeData.name}</p>
                            </div>
                            {/* Contact Row */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <p className="font-medium text-[14px] leading-[150%] text-[#98A2B3]">Mobile Number</p>
                                    <p className="font-medium text-[16px] leading-[150%] text-[#101828]">{employeeData.mobile}</p>
                                </div>
                                <div>
                                    <p className="font-medium text-[14px] leading-[150%] text-[#98A2B3]">Email</p>
                                    <p className="font-medium text-[16px] leading-[150%] text-[#101828]">{employeeData.email}</p>
                                </div>
                            </div>
                            {/* City & Work Field Row */}
                            {(employeeData.city || employeeData.workField) && (
                                <div className="grid grid-cols-2 gap-4">
                                    {employeeData.city && (
                                        <div>
                                            <p className="font-medium text-[14px] leading-[150%] text-[#98A2B3]">City</p>
                                            <p className="font-medium text-[16px] leading-[150%] text-[#101828]">{employeeData.city}</p>
                                        </div>
                                    )}
                                    {employeeData.workField && (
                                        <div>
                                            <p className="font-medium text-[14px] leading-[150%] text-[#98A2B3]">Work Field</p>
                                            <p className="font-medium text-[16px] leading-[150%] text-[#101828]">{employeeData.workField}</p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </ToolManagmentCard>

                {/* Company Information Card */}
                <ToolManagmentCard>
                    <h3 className="text-base font-bold text-gray-700 mb-3">Company information</h3>
                    <div className="border-b border-gray-100 mb-4"></div>

                    {isLoading ? (
                        <div className="flex items-center justify-center py-6">
                            <ThreeDotBouncingLoader />
                        </div>
                    ) : (
                        <div className="space-y-6">
                            {/* Name & Email Row */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <p className="font-medium text-[14px] leading-[150%] text-[#98A2B3]">Company Name</p>
                                    <p className="font-medium text-[16px] leading-[150%] text-[#101828]">{companyData.name}</p>
                                </div>
                                <div>
                                    <p className="font-medium text-[14px] leading-[150%] text-[#98A2B3]">Company Email</p>
                                    <p className="font-medium text-[16px] leading-[150%] text-[#101828]">{companyData.email}</p>
                                </div>
                            </div>
                            {/* Phone & Address Row */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <p className="font-medium text-[14px] leading-[150%] text-[#98A2B3]">Company Phone number</p>
                                    <p className="font-medium text-[16px] leading-[150%] text-[#101828]">{companyData.phone}</p>
                                </div>
                                <div>
                                    <p className="font-medium text-[14px] leading-[150%] text-[#98A2B3]">Address</p>
                                    <p className="font-medium text-[16px] leading-[150%] text-[#101828]">{companyData.address}</p>
                                </div>
                            </div>
                        </div>
                    )}
                </ToolManagmentCard>
            </div>

            {/* --- Assessment Result Section --- */}
            <ToolManagmentCard>
                <h3 className="text-base font-bold text-gray-700 mb-6 text-start">
                    Assessment Result
                <div className="border-b border-gray-100 mt-3"></div>
                </h3>

                {isLoading ? (
                    <div className="flex items-center justify-center py-8">
                        <ThreeDotBouncingLoader />
                    </div>
                ) : isBaselineResult ? (
                    <div>
                        <div className="grid grid-cols-3 gap-4">
                            {(scoreJson?.selected_questions || []).map((item, index) => (
                                <div
                                    key={item.id}
                                    className="bg-[#FF8E1C] transition-colors duration-200 rounded-lg px-6 py-2 h-[42px] text-white text-center font-medium text-base shadow-sm"
                                >
                                    {index + 1}. {item.text}
                                </div>
                            ))}
                        </div>
                    </div>
                ) : isNCodesTool ? (
                    <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">The E Codes Against Your 6 N Codes Are:</h3>

                        <div className="grid grid-cols-2 gap-4 mb-[18px]">
                            <div>
                                <h4 className="text-[#FF8E1C] font-bold text-lg text-start">N Code:</h4>
                            </div>
                            <div className="pl-4">
                                <h4 className="text-[#FF8E1C] font-bold text-lg text-end">E Code:</h4>
                            </div>
                        </div>

                        <div className="space-y-4">
                            {(ncodeSelected || []).map((item) => (
                                <div key={item.id || item.ncode || Math.random()} className="grid grid-cols-[49%_1%_49%] gap-0 bg-[#FFF4E5] p-4 rounded-[8px] items-center">
                                    <div>
                                        <p className="text-black text-[18px] leading-relaxed">{item.ncode || item.tool_options?.title || item.title}</p>
                                    </div>
                                    <div className="w-[1px] h-[100%] bg-black shrink-0" />
                                    <div className="pl-4">
                                        <p className="text-black text-[18px] leading-relaxed">{item.ecode || item.ecode_text || item.score || "-"}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ) : isHumanNeedsResult ? (
                    <div>
                        {/* <h3 className="text-xl font-bold text-gray-900 mb-4 text-start">Top Needs</h3> */}

                        <div className="rounded-lg border border-gray-200 overflow-hidden">
                            <Table>
                                <THead>
                                    <Tr className="bg-gray-50 text-left">
                                        <Th>S No.</Th>
                                        <Th>Personality Type</Th>
                                        <Th>Your Score</Th>
                                    </Tr>
                                </THead>
                                <TBody>
                                    {humanNeedsRows?.map((item, index) => (
                                        <Tr key={item.key} className="hover:bg-gray-50">
                                            <Td className="py-3 px-4 text-sm text-gray-700">{String(index + 1).padStart(2, "0")}</Td>
                                            <Td className="py-3 px-4 text-sm text-gray-700">{item.key}</Td>
                                            <Td className="py-3 px-4 text-sm text-gray-700">{item.value}</Td>
                                        </Tr>
                                    ))}
                                </TBody>
                            </Table>
                        </div>
                    </div>
                ) : isTraitsTestResult ? (
                    <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-4 text-start">Traits Test Results</h3>

                        <div className="rounded-lg border border-gray-200 overflow-hidden">
                            <Table>
                                <THead>
                                    <Tr className="bg-gray-50 text-left">
                                        <Th>S No.</Th>
                                        <Th>Trait</Th>
                                        <Th>Level</Th>
                                        <Th>Score</Th>
                                    </Tr>
                                </THead>
                                <TBody>
                                    {traitsTestRows?.map((item, index) => (
                                        <Tr key={item.name} className="hover:bg-gray-50">
                                            <Td className="py-3 px-4 text-sm text-gray-700">{String(index + 1).padStart(2, "0")}</Td>
                                            <Td>{item.name}</Td>
                                            <Td className="py-3 px-4 text-sm"><span>{String(item.level)}</span></Td>
                                            <Td className="py-3 px-4 text-sm"><div className="flex items-center gap-3"><span>{item.score}</span></div></Td>
                                        </Tr>
                                    ))}
                                </TBody>
                            </Table>
                        </div>
                    </div>
                ) : isPersonalityTestResult ? (
                    <div>
                        <div className="rounded-lg border border-gray-200 overflow-hidden">
                            <Table>
                                <THead>
                                    <Tr className="bg-gray-50 text-left">
                                        <Th className="py-3 px-4 ">Personality Type</Th>
                                        <Th className="py-3 px-4">Score</Th>
                                    </Tr>
                                </THead>
                                <TBody>
                                    {personalityTestRows?.map((item) => (
                                        <Tr key={item.key} className={`hover:bg-gray-50`}>
                                            <Td className={`py-3 px-4 text-sm ${item.value === highestScore ? 'text-orange-600 font-semibold' : 'text-gray-700'}`}>{item.key}</Td>
                                            <Td className="py-3 px-4 text-sm text-gray-700"><div className="flex items-center gap-3"><span className={`font-medium ${item.value === highestScore ? 'text-orange-600' : ''}`}>{item.value}</span></div></Td>
                                        </Tr>
                                    ))}
                                </TBody>
                            </Table>
                        </div>
                    </div>
                ) : (
                    <div className="rounded-lg overflow-hidden border border-gray-100">
                        <Table className="w-full text-sm text-gray-700">
                            <THead>
                                <Tr className="bg-[#FFF7EB] text-gray-700 text-xs uppercase tracking-wider">
                                    <Th className="py-4 px-6 text-left font-medium border-b border-gray-100 w-24">
                                        S No.
                                    </Th>
                                    <Th className="py-4 px-6 text-left font-medium border-b border-gray-100">
                                        Personality Type
                                    </Th>
                                    <Th className="py-4 px-6 text-left font-medium border-b border-gray-100">
                                        Your Score
                                    </Th>
                                </Tr>
                            </THead>

                            <TBody>
                                {personalityTypes.length > 0 ? (
                                    personalityTypes.map((item, index) => (
                                        <Tr
                                            key={index}
                                            className="border-b border-gray-50 last:border-b-0 hover:bg-gray-50 transition-colors"
                                        >
                                            <Td className="py-4 px-6 text-gray-600 font-medium">
                                                {String(index + 1).padStart(2, "0")}
                                            </Td>
                                            <Td className="py-4 px-6 text-gray-800 font-medium">
                                                {item.tool_options?.title || item.title || "-"}
                                            </Td>
                                            <Td className="py-4 px-6 text-gray-600 font-medium">
                                                {item.score || "0"}
                                            </Td>
                                        </Tr>
                                    ))
                                ) : (
                                    <Tr>
                                        <Td colSpan={3} className="text-center py-6 text-gray-500">
                                            No assessment results found
                                        </Td>
                                    </Tr>
                                )
                                }
                            </TBody>
                        </Table>
                    </div>
                )}
            </ToolManagmentCard>
        </div>
    );
};

export default CompanyEmployeeView;